import React, { FunctionComponent, useEffect, useCallback, useMemo, useRef, useState, useLayoutEffect } from 'react';
import useClickOutside from 'hooks/use-click-outside';
import Button from 'ui/atoms/button';
import Translate from 'ui/atoms/translate';
import ActionButtons from 'ui/molecules/action-buttons';
import { useFilterContext } from 'ui/molecules/filtering/hooks';
import useIsMedia, { DEVICES } from 'ui/hooks/use-is-media';
import * as Styled from './styled';
import { ReactNode } from 'react';
import Spacer from 'src/ui/atoms/spacer';
import Icon from 'src/ui/atoms/icon';
import { FilterState } from '../types';

interface FilterPopupProps {
  children: ReactNode;
  setFilterPopupHeight?: (height: number) => void;
  filtersNames?: string[];
  width?: number;
}

export const FilterPopup: FunctionComponent<FilterPopupProps> = ({
  children,
  filtersNames = [],
  width,
  setFilterPopupHeight,
}) => {
  const pickerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const draftFiltersRef = useRef<FilterState | null>(null);
  const {
    resetFilters,
    activeFilters,
    filterNumber: pageFilterNumber,
    entries,
    setApplyFilters,
    userFilters,
    discardFilterChanges,
  } = useFilterContext();
  const [showFilters, setShowFilters] = useState(false);

  const isPhone = useIsMedia(DEVICES.phone);

  const filterNumber = useMemo(() => {
    if (!filtersNames?.length || Object.keys(activeFilters).length === 0) return pageFilterNumber;

    return filtersNames.filter((filterName) => activeFilters[filterName])?.length;
  }, [JSON.stringify(activeFilters), JSON.stringify(filtersNames)]);

  const reset = () => {
    resetFilters(filtersNames);
    setShowFilters(false);
    setFilterPopupHeight?.(0);
    draftFiltersRef.current = null;
  };

  const applyFilters = () => {
    setApplyFilters(true);
    setShowFilters(false);
    setFilterPopupHeight?.(0);
    draftFiltersRef.current = null;
  };

  const closeAndDiscard = useCallback(() => {
    if (draftFiltersRef.current) {
      discardFilterChanges(draftFiltersRef.current);
    }
    setShowFilters(false);
    setFilterPopupHeight?.(0);
  }, [draftFiltersRef.current, discardFilterChanges, setFilterPopupHeight]);

  const onShowFilterToggle = useCallback(() => {
    setShowFilters((isOpen) => {
      if (isOpen) {
        closeAndDiscard();
      } else {
        const { search, ...popupOnlyFilters } = userFilters;
        draftFiltersRef.current = popupOnlyFilters;
      }

      return !isOpen;
    });
  }, [userFilters, closeAndDiscard]);

  useLayoutEffect(() => {
    if (!showFilters || !pickerRef.current) {
      return;
    }

    const updatePadding = () => {
      if (pickerRef.current) {
        const popoverHeight = pickerRef.current.offsetHeight;
        setFilterPopupHeight?.(popoverHeight);
      }
    };

    updatePadding();

    const resizeObserver = new ResizeObserver(() => {
      updatePadding();
    });

    resizeObserver.observe(pickerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [showFilters, setFilterPopupHeight]);

  useClickOutside([pickerRef, buttonRef], closeAndDiscard);

  useEffect(() => {
    if (!showFilters) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeAndDiscard();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showFilters, closeAndDiscard]);

  return (
    <Styled.Filters>
      <Styled.FilterBar>
        <Button
          onClick={onShowFilterToggle}
          active={showFilters}
          icon="filter"
          iconSize="regular"
          variant="secondary"
          buttonRef={buttonRef}
        >
          {!isPhone && (
            <>
              <Translate name="filters.buttons.filter" />
              {!!filterNumber && (
                <Styled.PopupTag padded="none" compact>
                  {filterNumber}
                </Styled.PopupTag>
              )}
            </>
          )}
        </Button>
      </Styled.FilterBar>
      {showFilters && (
        <Styled.FiltersPopover segmentRef={pickerRef} raised={true} $width={width}>
          <Styled.FiltersHeaderContainer>
            <Styled.FiltersHeader>
              <Translate name="filters.labels.filter" />
            </Styled.FiltersHeader>
            <Icon name="close-thin" size="large" color="grayDark" onClick={closeAndDiscard} />
          </Styled.FiltersHeaderContainer>
          <Spacer y={6} />
          {children}
          <ActionButtons>
            <Button onClick={reset} variant="link">
              <Translate name="filters.buttons.resetFilter" />
            </Button>
            <Button variant="primary" onClick={applyFilters}>
              <Translate name={'filters.buttons.showResults'} args={[entries]} />
            </Button>
          </ActionButtons>
        </Styled.FiltersPopover>
      )}
    </Styled.Filters>
  );
};

export default FilterPopup;
